import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams, useLocation } from "react-router-dom";
import useReveal from "@/hooks/useReveal";
import { PROJECTS as STATIC_PROJECTS, CATEGORIES } from "@/data/projects";
import { listProjects, GalleryProjectRow } from "@/lib/api";
import { U } from "@/data/services";

const HERO_BG = U("1492684223066-81342ee5ff30", 2400, 88);

const staticToCard = () => STATIC_PROJECTS.map(p => ({ slug: p.slug, title: p.title, category: p.category, hero: p.hero }));
const rowToCard = (r: GalleryProjectRow) => ({ slug: r.slug, title: r.title, category: r.category || "", hero: r.hero_image_url || "" });
const categoryKey = (cat?: string | null) => {
  if (!cat) return "other";
  const m: Record<string, string> = { "Corporate Events": "corporate", "Live Concerts": "concerts", "Government & Gala": "gala", "Exhibitions": "exhibitions", "Social Events": "social" };
  return m[cat] || "other";
};

const Gallery: React.FC = () => {
  useReveal();
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const [active, setActive] = useState<string>(params.get("cat") || "all");
  const [items, setItems] = useState<{ slug: string; title: string; category: string; hero: string }[]>([]);

  useEffect(() => {
    setItems(staticToCard());
    listProjects(true).then((rows: GalleryProjectRow[]) => { if (rows.length) setItems(rows.map(rowToCard)); }).catch(() => { setItems(staticToCard()); });
  }, [location.pathname]);
  useEffect(() => { setActive(params.get("cat") || "all"); }, [params]);

  const filtered = useMemo(() => active === "all" ? items : items.filter(p => categoryKey(p.category) === active), [items, active]);

  const set = (key: string) => { setActive(key); if (key === "all") setParams({}); else setParams({ cat: key }); };

  return (
    <main className="iav-page" data-testid="gallery-page">
      <section className="iav-page-hero iav-hero-bg" style={{ backgroundImage: `url(${HERO_BG})` }}>
        <div className="iav-page-hero-overlay" />
        <div className="iav-page-hero-inner">
          <span className="iav-tag-line" data-reveal>Portfolio</span>
          <h1 data-reveal>Our <span className="iav-accent">Work.</span></h1>
          <p className="lead" data-reveal>Selected events delivered across the Middle East and South Asia. Filter by category to explore.</p>
        </div>
      </section>

      <div className="iav-gal-filters" data-testid="gallery-filters">
        {CATEGORIES.map(c => (
          <button key={c.key} className={`iav-gal-filter ${active === c.key ? "is-active" : ""}`} onClick={() => set(c.key)} data-cursor="cta" data-testid={`gal-filter-${c.key}`}>{c.label}</button>
        ))}
      </div>

      <div className="iav-grid-3" data-testid="gallery-grid">
        {filtered.map(p => (
          <Link key={p.slug} to={`/gallery/${p.slug}`} className="iav-gal-card" data-cursor="cta" data-testid={`project-card-${p.slug}`}>
            {p.hero && <img src={p.hero} alt={p.title} loading="lazy" />}
            <div className="iav-gal-overlay" />
            <div className="iav-gal-meta">
              <span className="iav-gal-cat">{p.category}</span>
              <span className="iav-gal-title">{p.title}</span>
            </div>
          </Link>
        ))}
      </div>
    </main>
  );
};
export default Gallery;
